// react
import { List, Map } from "immutable";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

// components
import { Spinner } from "@performyard/py-components";

import Link from "../../../../components/Link";
import Spacer from "../../../../components/Spacer";
import Tabs from "../../../../components/Tabs";
import Typography from "../../../../components/Text";
import DeferredTiming from "./components/DeferredTiming";
// page specific components
import DigestTiming from "./components/DigestTiming";
import NotificationSetting from "./components/NotificationSetting";
import { NotificationChannels } from "./components/NotificationsChannels/NotificationChannels";

// hooks
import { useNotificationChannelSettings } from "../../../../api/hooks/defs/notification-settings";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import {
	updateNotificationChannelsSettings,
	updateTemplateNotificationChannelsSettings,
} from "../../../../api/services";

// actions
import {
	init as initAction,
	sendAdminDigestPatch,
	sendAsapAndDeferredPatch,
	sendDeferredTimingPatch,
	sendDigestPatch,
	sendWeeklyAdminDigestTimingPatch,
	sendWeeklyDigestTimingPatch,
	teardown as teardownAction,
	updateActiveSection,
} from "./actions";

// selectors
import { selectUserTimezone } from "../../../../selectors/app";
import {
	getAsapAndDeferredNotificationsGroupings,
	selectAdminDigestDeliverySettings,
	selectAdminWeeklyDigestNotificationsGroupings,
	selectDeferredFrequencySettings,
	selectDigestDeliverySettings,
	selectWeeklyDigestNotificationsGroupings,
} from "../../../../selectors/settings";
import {
	selectActiveSection,
	selectIsInitialized,
	selectRequestingNotifications,
	selectUserHasAdminDigest,
} from "./selectors";

// utils
import { areDeferredTimesEqual, areWeeklyDigestsEqual, parseTime } from "./utils";

// styles
import css from "./Notifications.module.scss";
// config
import config from "./config";

export const Notifications = (props) => {
	const {
		init,
		teardown,
		groupOrder,
		requestList,
		userTimezone = null,
		activeSection = null,
		isInitialized,
		deferredTimesProp,
		userHasAdminDigest,
		digestNotifications,
		digestDeliverySettings,
		updateDigestTimingPatch,
		updateDigestNotification,
		adminDigestNotifications,
		updateDeferredTimingPatch,
		adminDigestDeliverySettings,
		updateAdminDigestTimingPatch,
		asapAndDeferredNotifications,
		digestNotificationsGroupOrder,
		updateAdminDigestNotification,
		adminDigestNotificationsGroupOrder,
		updateAsapAndDeferredNotification,
		isTemplate = false,
	} = props;
	const [weeklyDigest, setWeeklyDigest] = useState(Map({ day: "monday", hour: 12 }));
	const [adminWeeklyDigest, setWeeklyAdminDigest] = useState(Map({ day: "monday", hour: 12 }));
	const [deferredTimes, setDeferredtimes] = useState(deferredTimesProp.map((item) => parseTime(item.hour)));
	const [featureFlags, setFeatureFlags] = useState({});

	// hide some notification groups based on feature flags
	const ffDependentGroups = {
		Goal: "ff-is-goals-enabled",
		Feedback: "ff-is-feedback-enabled",
	};

	const { value: GoalFeatureFlagValue } = useFeatureFlagQuery(ffDependentGroups.Goal, false);
	const { value: FeedbackFeatureFlagValue } = useFeatureFlagQuery(ffDependentGroups.Feedback, true);

	useEffect(() => {
		const flags = {
			Goal: GoalFeatureFlagValue,
			Feedback: FeedbackFeatureFlagValue,
		};
		setFeatureFlags(flags);
	}, [GoalFeatureFlagValue, FeedbackFeatureFlagValue]);

	const displayGroupOrder = groupOrder
		.map((groupName) => {
			if (!ffDependentGroups?.[groupName]) {
				return groupName;
			}
			return featureFlags?.[groupName] ? groupName : null;
		})
		.filter(Boolean);

	const [notificationChannelsState, setNotificationsChannelsState] = useState({
		"asap-daily": { enabledChannels: [] },
		digest: { enabledChannels: [] },
		"admin-digest": { enabledChannels: [] },
	});

	const cachedStates = useRef({
		deferredTimes: [],
		weeklyDigest: Map({ day: "monday", hour: 12 }),
		adminWeeklyDigest: Map({ day: "monday", hour: 12 }),
	});

	const {
		isFetching: notificationChannelsDataIsLoading,
		data: notificationChannelsData,
		refetch: revalidateNotificationChannelsData,
	} = useNotificationChannelSettings(isTemplate);

	useEffect(() => {
		if (!notificationChannelsData) {
			return;
		}

		const newNotificationChannelsState = Object.keys(notificationChannelsData).reduce((acc, key) => {
			acc[key] = { enabledChannels: notificationChannelsData[key].enabled_channels };
			return acc;
		}, {});

		setNotificationsChannelsState((prevState) => ({ ...prevState, ...newNotificationChannelsState }));
	}, [notificationChannelsData]);

	// on mount, init the view. teardown when unmounting.
	useEffect(() => {
		init();

		return () => {
			teardown();
		};
	}, []);

	// after initialize, update the deferred notitication times
	useEffect(() => {
		cachedStates.current.deferredTimes = deferredTimesProp.map((item) => parseTime(item.hour));
		setDeferredtimes(deferredTimesProp.map((item) => parseTime(item.hour)));
	}, [isInitialized]);

	// update the digest delivery settings when the prop updates (should happen
	// once)
	useEffect(() => {
		const parsedTime = digestDeliverySettings.get("hour", 9);
		setWeeklyDigest(
			Map({
				day: digestDeliverySettings.get("dayOfWeek"),
				hour: parsedTime,
			})
		);

		cachedStates.current.weeklyDigest = Map({
			day: digestDeliverySettings.get("dayOfWeek"),
			hour: parsedTime,
		});
	}, [digestDeliverySettings]);

	// update the admin digest delivery settings when the prop updates (should
	// happen once)
	useEffect(() => {
		const parsedTime = adminDigestDeliverySettings.get("hour", 9);
		setWeeklyAdminDigest(
			Map({
				day: adminDigestDeliverySettings.get("dayOfWeek"),
				hour: parsedTime,
			})
		);

		cachedStates.current.adminWeeklyDigest = Map({
			day: adminDigestDeliverySettings.get("dayOfWeek"),
			hour: parsedTime,
		});
	}, [adminDigestDeliverySettings]);

	// handles the saving of deferred notifications. Converts the value stored
	// in the state { hour, meridiem } to a 24hr time.
	const saveNotificationsTimingConfiguration = () => {
		const parsedTimes = deferredTimes.map((time) => {
			const { hour } = time;

			return {
				hour,
			};
		});

		updateDeferredTimingPatch(parsedTimes);
		cachedStates.current.deferredTimes = parsedTimes;
	};

	// handles the saving of the weekly digest. converts the value stored in
	// the state { dayOfWeek, hour, meridiem } to a format that is digestable
	// by the server { dayOfWeek, 24hrTime }
	const saveWeeklyDigestTimingSettings = () => {
		const hour = weeklyDigest.get("hour");
		updateDigestTimingPatch(weeklyDigest.get("day"), hour);
		cachedStates.current.weeklyDigest = weeklyDigest;
	};

	// handles the saving of the admin weekly digest. converts the value stored
	// in the state { dayOfWeek, hour, meridiem } to a format that is
	// digestable by the server { dayOfWeek, 24hrTime }
	const saveWeeklyAdminDigestTimingSettings = () => {
		const hour = adminWeeklyDigest.get("hour");
		updateAdminDigestTimingPatch(adminWeeklyDigest.get("day"), hour);
		cachedStates.current.adminWeeklyDigest = adminWeeklyDigest;
	};

	// determines which notifications channels are elegible to activate.
	const selectElegibleChannels = (activeSection) => {
		switch (activeSection) {
			case "asap-daily":
				return ["email", "ms-teams"];
			case "digest":
				return ["email"];
			case "admin-digest":
				return ["email"];
			default:
				return [];
		}
	};

	const handleToggleChannel = async (notificationType, enabledChannels) => {
		setNotificationsChannelsState({
			...notificationChannelsState,
			[notificationType]: { enabledChannels },
		});

		if (isTemplate) {
			await updateTemplateNotificationChannelsSettings(notificationType, enabledChannels);
		} else {
			await updateNotificationChannelsSettings(notificationType, enabledChannels);
		}

		revalidateNotificationChannelsData();
	};

	// spin if not initialized
	if (!isInitialized) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner size={20} />
			</div>
		);
	}

	return (
		<div className={css.main}>
			<div>
				<Spacer height={2} />
				<Tabs
					className={css.tabs}
					onSelectTitle={(id) => {
						props.updateActiveSection(id);
					}}
					activeTitle={activeSection}
					titles={[
						{
							id: "asap-daily",
							label: "Daily",
						},
						{
							id: "digest",
							label: "Weekly",
						},
						...(userHasAdminDigest
							? [
									{
										id: "admin-digest",
										label: "Admin Weekly",
									},
								]
							: []),
					]}
				/>

				{activeSection === "asap-daily" ? (
					<div className={`${css.sectionWrapper} ${css.asapDailySection}`}>
						<div className={css.sectionDescription}>
							<Typography size="paragraph-2" weight="semi-bold" color="neutral-9">
								Select Notifications to receive and when.
							</Typography>

							<ul className={css.list}>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select As Soon As Possible to be notified about a topic within 20 minutes.
									</Typography>
								</li>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select Daily to have notifications grouped in a single email either once or
										twice a day.
									</Typography>
								</li>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select Off to not receive email notifications about a topic.
									</Typography>
								</li>
							</ul>
							<Spacer height={3} />
							{userTimezone ? (
								<div>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Your timezone is currently set to: <span>{userTimezone}</span>. You can change
										your timezone settings{" "}
										<Link className={css.changeTZLink} href="/settings/timezone/">
											here.
										</Link>
									</Typography>
									<Spacer height={3} />
								</div>
							) : null}

							<NotificationChannels
								eligibleChannels={selectElegibleChannels(activeSection)}
								// if present, use the state value, otherwise use empty array
								enabledNotificationChannels={
									notificationChannelsState[`${activeSection}`]["enabledChannels"] || []
								}
								onChangeEnabledNotificationChannels={handleToggleChannel}
								notificationType={activeSection}
								isLoading={notificationChannelsDataIsLoading}
							/>

							<Spacer height={1} />
							<DeferredTiming
								timezone={userTimezone}
								deferredTimes={deferredTimes}
								setDeferredtimes={setDeferredtimes}
								saveNotificationsTimingConfiguration={
									!areDeferredTimesEqual(cachedStates.current.deferredTimes, deferredTimes)
										? saveNotificationsTimingConfiguration
										: null
								}
							/>
						</div>

						<div className={css.settingsTableContainer}>
							<div className={css.tableBody}>
								{displayGroupOrder.map((groupName) => (
									<div
										data-testid={`group-${groupName}`}
										key={groupName}
										className={css.groupContainer}
									>
										<div className={css.groupHeadContainer}>
											<div className={css.groupName}>{groupName}</div>

											<div className={css.tableHead}>
												<div className={`${css.headCell} ${css.toggleSwitchCell}`}>
													As Soon As Possible
												</div>
												<div className={`${css.headCell} ${css.toggleSwitchCell}`}>Daily</div>
												<div className={`${css.headCell} ${css.toggleSwitchCell}`}>Off</div>
											</div>
										</div>

										{asapAndDeferredNotifications.getIn([groupName], List()).map((notification) => {
											const id = notification.get("name");
											const state = notification.get("state");

											return (
												<NotificationSetting
													key={id}
													className={css.tableRow}
													name={id}
													label={notification.get("label")}
													state={state}
													options={config.asapAndDeferredOptions}
													requestingNotification={requestList.getIn([id], false)}
													onChange={(notificationType) =>
														updateAsapAndDeferredNotification(id, notificationType)
													}
												/>
											);
										})}
									</div>
								))}
							</div>
						</div>
					</div>
				) : null}

				{activeSection === "digest" ? (
					<div className={`${css.sectionWrapper} ${css.asapDailySection}`}>
						<div className={css.sectionDescription}>
							<Typography size="paragraph-2" weight="semi-bold" color="neutral-9">
								Select a day of the week and time for the weekly digest.
							</Typography>

							<ul className={css.list}>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select On to include a topic in the email.
									</Typography>
								</li>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select Off to exclude a topic from the email.
									</Typography>
								</li>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select Off for each topic to disable the weekly digest.
									</Typography>
								</li>
							</ul>
							<Spacer height={3} />
							{userTimezone ? (
								<div>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Your timezone is currently set to: <span>{userTimezone}</span>. You can change
										your timezone settings{" "}
										<Link className={css.changeTZLink} href="/settings/timezone/">
											here.
										</Link>
									</Typography>
								</div>
							) : null}
						</div>

						<DigestTiming
							timezone={userTimezone}
							label="When do you want to receive the weekly digest?"
							weeklyDigest={weeklyDigest}
							setWeeklyDigest={setWeeklyDigest}
							saveWeeklyDigestTimingSettings={
								!areWeeklyDigestsEqual(cachedStates.current.weeklyDigest.toJS(), weeklyDigest.toJS())
									? saveWeeklyDigestTimingSettings
									: null
							}
						/>

						<div className={css.settingsTableContainer}>
							<div className={css.tableBody}>
								{digestNotificationsGroupOrder.map((groupName) => (
									<div key={groupName} className={css.groupContainer}>
										<div className={css.groupHeadContainer}>
											<div className={css.groupName}>{groupName}</div>

											<div className={css.tableHead}>
												<div className={`${css.headCell} ${css.toggleSwitchCell}`}>On</div>
												<div className={`${css.headCell} ${css.toggleSwitchCell}`}>Off</div>
											</div>
										</div>

										{digestNotifications.getIn([groupName], List()).map((notification) => {
											const id = notification.get("name");
											const state = notification.get("state");

											return (
												<NotificationSetting
													key={id}
													className={css.tableRow}
													name={id}
													label={notification.get("label")}
													state={state}
													options={config.digestOptions}
													requestingNotification={requestList.getIn([id], false)}
													onChange={(notificationType) =>
														updateDigestNotification(id, notificationType)
													}
												/>
											);
										})}
									</div>
								))}
							</div>
						</div>
					</div>
				) : null}

				{userHasAdminDigest && activeSection === "admin-digest" ? (
					<div className={`${css.sectionWrapper} ${css.asapDailySection}`}>
						<div className={css.sectionDescription}>
							<Typography size="paragraph-2" weight="semi-bold" color="neutral-9">
								Select a day of the week and time for the administrator weekly digest.
							</Typography>

							<ul className={css.list}>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select On to include a topic in the email.
									</Typography>
								</li>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select Off to exclude a topic from the email.
									</Typography>
								</li>
								<li>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Select Off for each topic to disable the administrator weekly digest.
									</Typography>
								</li>
							</ul>
							<Spacer height={3} />
							{userTimezone ? (
								<div>
									<Typography size="paragraph-2" weight="regular" color="neutral-9">
										Your timezone is currently set to: <span>{userTimezone}</span>. You can change
										your timezone settings{" "}
										<Link className={css.changeTZLink} href="/settings/timezone/">
											here.
										</Link>
									</Typography>
								</div>
							) : null}
						</div>

						<DigestTiming
							timezone={userTimezone}
							label="When do you want to receive the admin weekly digest?"
							weeklyDigest={adminWeeklyDigest}
							setWeeklyDigest={setWeeklyAdminDigest}
							saveWeeklyDigestTimingSettings={
								!areWeeklyDigestsEqual(
									cachedStates.current.adminWeeklyDigest.toJS(),
									adminWeeklyDigest.toJS()
								)
									? saveWeeklyAdminDigestTimingSettings
									: null
							}
						/>

						<div className={css.settingsTableContainer}>
							<div className={css.tableBody}>
								{adminDigestNotificationsGroupOrder.map((groupName) => (
									<div key={groupName} className={css.groupContainer}>
										<div className={css.groupHeadContainer}>
											<div className={css.groupName} />

											<div className={css.tableHead}>
												<div className={`${css.headCell} ${css.toggleSwitchCell}`}>On</div>
												<div className={`${css.headCell} ${css.toggleSwitchCell}`}>Off</div>
											</div>
										</div>

										{adminDigestNotifications.getIn([groupName], List()).map((notification) => {
											const id = notification.get("name");
											const state = notification.get("state");

											return (
												<NotificationSetting
													key={id}
													className={css.tableRow}
													name={id}
													label={notification.get("label")}
													state={state}
													options={config.adminDigestOptions}
													requestingNotification={requestList.getIn([id], false)}
													onChange={(notificationType) =>
														updateAdminDigestNotification(id, notificationType)
													}
												/>
											);
										})}
									</div>
								))}
							</div>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

Notifications.propTypes = {
	/** if truthy, renders the value beneath time pickers */
	userTimezone: PropTypes.string,
	/** action to run on mount */
	init: PropTypes.func.isRequired,
	/** action to run on unmount */
	teardown: PropTypes.func.isRequired,
	/** action when the user saves their deferred times */
	updateDeferredTimingPatch: PropTypes.func.isRequired,
	/** action when user updates a notification to be asap, deferred, or off */
	updateAsapAndDeferredNotification: PropTypes.func.isRequired,
	/** action when user saves their weekly digest times */
	updateDigestTimingPatch: PropTypes.func.isRequired,
	/** action when user updates a digest notification to be on or off */
	updateDigestNotification: PropTypes.func.isRequired,
	/** action when user updates an admin digest notification to be on or off */
	updateAdminDigestNotification: PropTypes.func.isRequired,
	/** action when user saves the admin digest notification time  */
	updateAdminDigestTimingPatch: PropTypes.func.isRequired,
	/** action to update the active section */
	updateActiveSection: PropTypes.func.isRequired,
	/** have all the requests settled, it is initialized */
	isInitialized: PropTypes.bool.isRequired,
	/** show the admin digest */
	userHasAdminDigest: PropTypes.bool.isRequired,
	/** deferred notification setting times */
	deferredTimesProp: PropTypes.array.isRequired,
	/** weekly digest setting times */
	digestDeliverySettings: PropTypes.instanceOf(Map).isRequired,
	/** asap and deferred notification settings */
	asapAndDeferredNotifications: PropTypes.instanceOf(Map).isRequired,
	/** list of requests we are awaiting for */
	requestList: PropTypes.instanceOf(Map).isRequired,
	/** weekly digest notification settings */
	digestNotifications: PropTypes.instanceOf(Map).isRequired,
	/** weekly admin digest notification settings */
	adminDigestNotifications: PropTypes.instanceOf(Map).isRequired,
	/** weekly admin digest timing settings */
	adminDigestDeliverySettings: PropTypes.instanceOf(Map).isRequired,
	/** group order for asap and deferred notifications */
	groupOrder: PropTypes.instanceOf(List).isRequired,
	/** group order for weekly digest notifications */
	digestNotificationsGroupOrder: PropTypes.instanceOf(List).isRequired,
	/** group order for admin weekly digest notifications */
	adminDigestNotificationsGroupOrder: PropTypes.instanceOf(List).isRequired,
	/** name of the active section */
	activeSection: PropTypes.oneOf(["asap-daily", "digest", "admin-digest"]),
	isTemplate: PropTypes.bool,
};

const mapStateToProps = (state) => {
	const digestNotificationGrouping = selectWeeklyDigestNotificationsGroupings(state);
	const asapDeferredNotificationGroupings = getAsapAndDeferredNotificationsGroupings(state);
	const adminDigestNotificationGrouping = selectAdminWeeklyDigestNotificationsGroupings(state);

	return {
		userTimezone: selectUserTimezone(state),
		isInitialized: selectIsInitialized(state),
		activeSection: selectActiveSection(state),
		userHasAdminDigest: selectUserHasAdminDigest(state),
		requestList: selectRequestingNotifications(state),
		digestNotificationsGroupOrder: digestNotificationGrouping.categories,
		digestNotifications: digestNotificationGrouping.notifications,
		adminDigestNotificationsGroupOrder: adminDigestNotificationGrouping.categories,
		adminDigestNotifications: adminDigestNotificationGrouping.notifications,
		groupOrder: asapDeferredNotificationGroupings.categories,
		deferredTimesProp: selectDeferredFrequencySettings(state).toJS(),
		asapAndDeferredNotifications: asapDeferredNotificationGroupings.notifications,
		digestDeliverySettings: selectDigestDeliverySettings(state, Map({ day: "monday", hour: 12, meridiem: "am" })),
		adminDigestDeliverySettings: selectAdminDigestDeliverySettings(
			state,
			Map({ day: "monday", hour: 12, meridiem: "am" })
		),
	};
};

const mapDispatchToProps = (dispatch) => ({
	init: () => dispatch(initAction()),
	teardown: () => dispatch(teardownAction()),
	updateActiveSection: (sectionName) => dispatch(updateActiveSection(sectionName)),
	updateDigestNotification: (notification, type) => dispatch(sendDigestPatch(notification, type)),
	updateDeferredTimingPatch: (notificationTimes) => dispatch(sendDeferredTimingPatch(notificationTimes)),
	updateAdminDigestNotification: (notification, type) => dispatch(sendAdminDigestPatch(notification, type)),
	updateDigestTimingPatch: (dayOfWeek, hour) => dispatch(sendWeeklyDigestTimingPatch(dayOfWeek, hour)),
	updateAsapAndDeferredNotification: (notification, type) => dispatch(sendAsapAndDeferredPatch(notification, type)),
	updateAdminDigestTimingPatch: (dayOfWeek, hour) => dispatch(sendWeeklyAdminDigestTimingPatch(dayOfWeek, hour)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
